/**
 * Tabelas de valores — a especificação numérica publicada.
 *
 * São **4.859 tabelas com 18.417 linhas**, ligadas a **6.459 veículos**. Cada
 * tabela tem uma configuração de colunas própria (13.593 configurações no
 * total): uma tabela de fluidos tem colunas diferentes de uma de folgas, e é a
 * `qtdColunas` de cada uma que diz quantas.
 *
 * Por isso a grade de linhas é **montada a partir da configuração da tabela
 * aberta**, e não de um conjunto fixo de colunas — cada linha é um vetor de
 * valores cujo significado só a configuração conhece.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import {
  usarFilhos, usarGravar, usarIncluir, usarMestre, usarPublicacao,
  type LinhaDaTabela, type TabelaDeValores, type VeiculoVinculado,
} from "@/api/outrosProjetos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { usarOrdemDaLista, usarRecemIncluido } from "@/kit/ordemDaLista"
import { ErroDaApi } from "@/api/erros"
import {
  Botao, Campo, ColunaRecolhivel, Escolha, EstadoDePublicacao, FiltroDaLista, ItemDaLista, Linha, Paginacao, Painel, Secao,
  TabelaSimples, Vazio, anosInvertidos, estiloCampo, mensagem, nomeOu, usarNomes, usarTermoAdiado,
  type ColunaSimples,
} from "./pecas"
import { PinoutsDaLinha } from "./PinoutsDaLinha"

/**
 * Os tres tipos, com os rotulos do sistema antigo (`Views/Tabela/Incluir.cshtml`) --
 * copiados de la de proposito, porque quem cadastra ja conhece esses nomes. O acervo
 * hoje: 2.874 do tipo 1, 1.872 do tipo 2 e 114 do tipo 0.
 */
const TIPOS: [number, string][] = [
  [1, "Tabela de especificações"],
  [2, "Fluidos e lubrificantes"],
  [0, "Tabela de valores"],
]

const POR_PAGINA = 50

const VEICULO_VAZIO = { veiculoId: 0, motorId: 0, anoInicial: 0, anoFinal: 0 }

/** Uma coluna da grade de linhas: a posição no vetor `valores` e o título. */
type ColunaDeValor = { indice: number; rotulo: string }

/** A linha como a grade a lê: um campo `v0`, `v1`… por coluna, para o filtro achar o valor. */
type LinhaNaGrade = { id: number; ordem: number; linha: LinhaDaTabela; [coluna: string]: unknown }

/** O ano de um vínculo da tabela, que a API devolve como data (`2022-01-01T00:00:00`). */
function anoDe(data: unknown): string {
  return data ? String(data).slice(0, 4) : "—"
}

export function TabelasDeValores() {
  const [busca, setBusca] = useState("")
  const termo = usarTermoAdiado(busca)
  const [pagina, setPagina] = useState(1)
  // A lista pagina (4.868 tabelas): a alfabética é feita na API, como em Torques — ordenar
  // aqui embaralharia só a página aberta.
  const [ordem, setOrdem] = usarOrdemDaLista("tabelas-valores")
  // Busca ou ordem nova começam da primeira página.
  useEffect(() => setPagina(1), [termo, ordem])
  const [aberta, setAberta] = useState<TabelaDeValores | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const nomes = usarNomes()
  const lista = usarMestre<TabelaDeValores>("tabelas-valores", "api/tabelas-especificacao", {
    busca: termo || undefined, ordem: ordem === "alfabetica" ? "alfabetica" : undefined,
    pagina, pageSize: POR_PAGINA,
  })
  // A ficha lê a versão da lista, e não a guardada no clique: depois de gravar os títulos
  // das colunas a lista relê, e a grade e os campos da linha passam a usar os nomes novos
  // sem precisar reabrir a tabela.
  const atual = aberta ? (lista.itens.find((t) => t.id === aberta.id) ?? aberta) : null

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Tabelas de valores" detalhe={`${lista.total.toLocaleString("pt-BR")} tabelas`}>
        <Botao destaque aoClicar={() => { setRecado(null); setAberta(null); setIncluindo(true) }}>
          Incluir tabela
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="As tabelas de valores são conteúdo publicado" />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Filtro no topo da coluna, como em Relés e fusíveis: mora com a lista que ele muda. */}
        <ColunaRecolhivel chave="tabelas-valores" titulo="Tabelas" largura={330}
                          detalhe={termo ? `${lista.total.toLocaleString("pt-BR")} achadas` : undefined}
                          topo={<FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Filtrar tabelas"
                                               placeholder="Título da tabela…"
                                               ordem={ordem} aoMudarOrdem={setOrdem} />}>
          {lista.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
              {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
            </p>
          ) : lista.itens.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nada com esse texto.</p>
          ) : (
            <>
              {lista.itens.map((t) => (
                <ItemDaLista
                  key={t.id}
                  ativo={aberta?.id === t.id}
                  marcado={t.publicado}
                  aoClicar={() => setAberta(t)}
                  titulo={t.titulo || `Tabela ${t.id}`}
                  detalhe={`${t.qtdColunas} colunas · ${nomeOu(nomes.sistema, t.sistemaId, "sistema")}`}
                />
              ))}
              <Paginacao pagina={pagina} aoMudar={setPagina}
                         temProxima={pagina < Math.ceil(lista.total / POR_PAGINA)}
                         totalDePaginas={Math.max(1, Math.ceil(lista.total / POR_PAGINA))} />
            </>
          )}
        </ColunaRecolhivel>

        {incluindo ? (
          <Inclusao
            nomes={nomes}
            aoAvisar={setRecado}
            aoFechar={() => setIncluindo(false)}
            aoCriar={(t) => { setIncluindo(false); setAberta(t) }}
          />
        ) : atual ? (
          // `key`: a linha em edição e a que está sendo digitada são de uma tabela só.
          <Ficha key={atual.id} tabela={atual} nomes={nomes} aoAvisar={setRecado}
                 aoFechar={() => setAberta(null)} />
        ) : (
          <Vazio>
            Escolha uma tabela à esquerda. Cada uma tem sua própria configuração de colunas —
            uma de fluidos não tem as mesmas colunas de uma de folgas.
          </Vazio>
        )}
      </div>

      <BarraDeEstado>{recado ?? ""}</BarraDeEstado>
    </div>
  )
}

/**
 * Incluir tabela de valores.
 *
 * **As colunas entram aqui, e não depois.** Uma tabela criada sem configuração mostra
 * "Coluna 1", "Coluna 2" na grade, e nomear no nascimento é o caminho de quem está
 * cadastrando. A ficha também edita os títulos, para as 4.859 que já existem.
 *
 * O que ficou de fora: a **grandeza** por coluna, que o formulário antigo oferece. Não
 * existe rota para listar grandezas na API, e o acervo diz que isso quase não é usado —
 * **10 configurações de coluna com grandeza preenchida, de 13.596**. Inventar um campo
 * de texto livre no lugar de um catálogo seria piorar o dado.
 *
 * Os rótulos de tipo são os do sistema antigo. O padrão de 3 colunas também vem do
 * acervo: 2.995 tabelas têm 3, e é a escolha mais provável de quem cadastra.
 */
function Inclusao({ nomes, aoAvisar, aoFechar, aoCriar }: {
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
  aoCriar: (t: TabelaDeValores) => void
}) {
  const incluir = usarIncluir<TabelaDeValores>([["outros", "tabelas-valores"]])
  const [forma, setForma] = useState({ titulo: "", tipo: 1, sistemaId: 0, qtdColunas: 3, texto: "" })
  const [titulos, setTitulos] = useState<string[]>(["", "", ""])

  // A lista de títulos acompanha a quantidade sem perder o que já foi digitado: quem
  // escreveu quatro nomes e voltou para três não deve perder os três primeiros.
  const mudarQuantidade = (n: number) => {
    const quantas = Math.max(1, Math.min(12, n || 1))
    setForma({ ...forma, qtdColunas: quantas })
    setTitulos((antes) => Array.from({ length: quantas }, (_, i) => antes[i] ?? ""))
  }

  const falta = !forma.titulo.trim() || forma.sistemaId <= 0

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>Incluir tabela</b>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      <Secao titulo="A tabela">
        <Linha>
          <Campo rotulo="Título">
            <input autoFocus value={forma.titulo} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, titulo: e.target.value })} />
          </Campo>
          <Escolha rotulo="Sistema" mapa={nomes.sistema} prefixo="sistema"
                   valor={forma.sistemaId} aoMudar={(v) => setForma({ ...forma, sistemaId: v })} />
        </Linha>
        <Linha>
          <Campo rotulo="Tipo">
            {/* Select simples, e não o `Escolha`: ele descarta id 0 de propósito (0 é
                "nenhum" nos catálogos), e aqui 0 é um tipo legítimo. */}
            <select
              value={forma.tipo}
              onChange={(e) => setForma({ ...forma, tipo: Number(e.target.value) })}
              style={estiloCampo}
            >
              {TIPOS.map(([v, rotulo]) => <option key={v} value={v}>{rotulo}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Colunas" ajuda="Quantas colunas de valor a tabela tem.">
            <input type="number" min={1} max={12} value={forma.qtdColunas} style={estiloCampo}
                   onChange={(e) => mudarQuantidade(Number(e.target.value))} />
          </Campo>
        </Linha>
        <Campo rotulo="Texto" ajuda="Observação que aparece junto da tabela.">
          <input value={forma.texto} style={estiloCampo}
                 onChange={(e) => setForma({ ...forma, texto: e.target.value })} />
        </Campo>
      </Secao>

      <Secao titulo="Os títulos das colunas" contagem={`${forma.qtdColunas}`}>
        <p style={{ margin: "0 0 7px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5,
                    maxWidth: "74ch" }}>
          Sem título, a coluna aparece como "Coluna 1" na grade. Dá para renomear depois na
          ficha, mas vale escrever agora.
        </p>
        {titulos.map((t, i) => (
          <Campo key={i} rotulo={`Coluna ${i + 1}`}>
            <input
              value={t}
              placeholder={i === 0 ? "Ex.: Medida" : undefined}
              onChange={(e) => setTitulos(titulos.map((x, j) => (j === i ? e.target.value : x)))}
              style={estiloCampo}
            />
          </Campo>
        ))}

        <p style={{ margin: "8px 0 10px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
          A tabela nasce <b>fora do ar</b>. Depois de criar, a ficha abre aqui para as linhas de
          valor e os veículos.
        </p>

        <div style={{ display: "flex", gap: 6 }}>
          <Botao
            destaque
            ocupado={incluir.isPending}
            desligado={falta}
            aoClicar={() => {
              aoAvisar(null)
              incluir.mutate({
                rota: "api/tabelas-especificacao",
                dados: {
                  titulo: forma.titulo.trim(), tipo: forma.tipo, texto: forma.texto,
                  qtdColunas: forma.qtdColunas, sistemaId: forma.sistemaId, publicado: false,
                  colunas: titulos.map((t, i) => ({
                    coluna: i + 1, tipoCampo: 0, tolerancia: 0,
                    grandeza: "", grandezaId: 0, titulo: t.trim(),
                  })),
                },
              }, {
                onSuccess: (t) => {
                  aoAvisar(`Tabela ${t.id} criada, fora do ar. Acrescente as linhas.`)
                  aoCriar(t)
                },
                onError: (e) => aoAvisar(mensagem(e)),
              })
            }}
          >
            {incluir.isPending ? "Criando…" : "Criar a tabela"}
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </div>
      </Secao>
    </Painel>
  )
}

/** O que o "Gravar" da tabela leva: serve para dizer se há alteração não gravada. */
const CAMPOS_DA_TABELA = ["titulo", "texto", "qtdColunas", "sistemaId", "colunas"] as const

function Ficha({ tabela, nomes, aoAvisar, aoFechar }: {
  tabela: TabelaDeValores
  nomes: ReturnType<typeof usarNomes>
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const base = `api/tabelas-especificacao/${tabela.id}`
  const linhas = usarFilhos<LinhaDaTabela>(["tabela-linhas", tabela.id], `${base}/linhas`)
  const veiculos = usarFilhos<VeiculoVinculado>(["tabela-veiculos", tabela.id], `${base}/veiculos`)

  const invalidar = [["outros", "tabelas-valores"],
                     ["outros-filhos", "tabela-linhas", tabela.id],
                     ["outros-filhos", "tabela-veiculos", tabela.id]]
  const { alterar, criar, excluir } = usarGravar(invalidar)
  const publicacao = usarPublicacao(invalidar)
  // As linhas gravam pelo seu próprio par de mutações: com o mesmo `alterar` da tabela,
  // gravar uma linha deixava o "Gravar" da tabela dizendo "Gravando…" também.
  const daLinha = usarGravar([["outros-filhos", "tabela-linhas", tabela.id]])

  // O formulário só recomeça quando muda a tabela ou o que está GRAVADO (como no Torques). Por
  // referência, publicar, vincular veículo ou gravar uma linha renovavam a lista, vinha um
  // objeto novo e o título digitado voltava ao anterior (Feedback 34).
  const [forma, setForma] = useState({ ...tabela })
  const gravado = JSON.stringify(CAMPOS_DA_TABELA.map((k) => tabela[k] ?? (k === "colunas" ? [] : "")))
  useEffect(() => setForma({ ...tabela }), [tabela.id, gravado])
  const mudou = JSON.stringify(CAMPOS_DA_TABELA.map((k) => forma[k] ?? (k === "colunas" ? [] : ""))) !== gravado

  /** Os dados do PUT. Sem `publicado`: a API só mexe na publicação quando o campo vem, e o formulário pode estar com o valor de antes do "Publicar". */
  function gravarTabela(aoGravar: () => void) {
    const { publicado: _, ...dados } = forma
    alterar.mutate({ rota: base, dados }, {
      onSuccess: aoGravar,
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  const [novoVeiculo, setNovoVeiculo] = useState(VEICULO_VAZIO)

  /**
   * A linha clicada, em edição — e com ela o painel de pinouts, que mora dentro da
   * edição. Até 13/10/2026 a grade só mostrava: a linha em branco nascia com o recado
   * "preencha e grave" e não havia onde preencher.
   *
   * `valores` guarda `null` onde a célula não existe: a célula que ninguém tocou volta
   * para a API como veio, e não vira texto vazio só porque a linha foi aberta.
   */
  const [emEdicao, setEmEdicao] = useState<{ id: number; ordem: number; valores: (string | null)[] } | null>(null)
  const edicao = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (emEdicao) edicao.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" })
  }, [emEdicao?.id])

  // A linha nova é digitada inteira antes de ir: "incluir em branco e preencher depois"
  // deixava linha vazia na tabela publicada sempre que a pessoa desistia no meio.
  const [nova, setNova] = useState<(string | null)[]>([])
  const primeiroDaNova = useRef<HTMLInputElement>(null)
  const recente = usarRecemIncluido()

  /**
   * As colunas da grade saem da configuração da tabela. Sem título configurado,
   * a coluna recebe o número — melhor que um cabeçalho em branco.
   */
  const colunas = useMemo(() => {
    const config = tabela.colunas ?? []
    const quantas = Math.max(tabela.qtdColunas || 0, config.length)
    return Array.from({ length: quantas }, (_, i) => {
      const c = config.find((x) => x.coluna === i + 1) ?? config[i]
      const rotulo = [c?.titulo, c?.grandeza].filter((x) => x && String(x).trim()).join(" · ")
      return { indice: i, rotulo: rotulo || `Coluna ${i + 1}` }
    })
  }, [tabela])

  const grade = useMemo(() => (linhas.data ?? []).map((l) => {
    const g: LinhaNaGrade = { id: l.id, ordem: l.ordem, linha: l }
    colunas.forEach((c) => { g[`v${c.indice}`] = l.valores?.[c.indice] ?? "" })
    return g
  }), [linhas.data, colunas])

  const colunasDaGrade: ColunaSimples<LinhaNaGrade>[] = [
    { chave: "ordem", rotulo: "#", largura: 50 },
    ...colunas.map((c) => ({ chave: `v${c.indice}`, rotulo: c.rotulo })),
    {
      chave: "id", rotulo: "", largura: 50,
      render: (g) => (
        <button
          aria-label={`Remover a linha ${g.ordem}`}
          onClick={(e) => {
            // A linha inteira abre a edição; o × não pode abrir junto.
            e.stopPropagation()
            aoAvisar(null)
            excluir.mutate(`${base}/linhas/${g.id}`, {
              onSuccess: () => {
                aoAvisar("Linha removida.")
                setEmEdicao((a) => (a?.id === g.id ? null : a))
              },
              onError: (x) => aoAvisar(mensagem(x)),
            })
          }}
          style={{
            border: 0, background: "transparent", cursor: "pointer",
            color: "var(--txt3)", fontSize: 13, lineHeight: 1,
          }}
        >
          ×
        </button>
      ),
    },
  ]

  const abrirLinha = (l: LinhaDaTabela) => {
    aoAvisar(null)
    // Tantos valores quantas colunas — e nunca menos do que a linha já tem: o PUT troca
    // as células todas, e cortar aqui apagaria o que está além da configuração atual.
    const quantas = Math.max(colunas.length, l.valores?.length ?? 0)
    setEmEdicao({
      id: l.id, ordem: l.ordem,
      valores: Array.from({ length: quantas }, (_, i) => l.valores?.[i] ?? null),
    })
  }

  const gravarLinha = () => {
    if (!emEdicao || daLinha.alterar.isPending) return
    aoAvisar(null)
    daLinha.alterar.mutate({
      rota: `${base}/linhas/${emEdicao.id}`,
      dados: { ordem: emEdicao.ordem, valores: emEdicao.valores },
    }, {
      onSuccess: () => { aoAvisar("Linha gravada."); setEmEdicao(null) },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  const valoresDaNova = colunas.map((c) => nova[c.indice] ?? "")
  const novaVazia = valoresDaNova.every((v) => !v?.trim())

  const incluirLinha = () => {
    if (novaVazia || daLinha.criar.isPending) return
    aoAvisar(null)
    // Sem `ordem`: a API põe no fim (maior ordem + 1). Contar as linhas aqui repetia o
    // número sempre que uma do meio tinha sido removida.
    daLinha.criar.mutate({ rota: `${base}/linhas`, dados: { valores: valoresDaNova } }, {
      onSuccess: (criada) => {
        setNova([])
        aoAvisar("Linha incluída.")
        recente.marcar((criada as LinhaDaTabela | undefined)?.id)
        primeiroDaNova.current?.focus()
      },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>{tabela.titulo}</b>
        <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>{tabela.id}</span>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      {/* **Publicar é botão próprio, e não um checkbox do formulário.** Até 27/10/2026 havia
          uma caixa "Publicado" no meio dos campos, que só valia depois do "Gravar" —
          quem cadastrava a tabela inteira não achava como pôr no ar. */}
      <EstadoDePublicacao
        publicado={tabela.publicado}
        oQue="a tabela"
        ocupado={publicacao.isPending}
        aoTrocar={(publicar) => {
          aoAvisar(null)
          const publicarAgora = () => publicacao.mutate({ base, publicar }, {
            onSuccess: () => aoAvisar(publicar ? "Tabela no ar." : "Tabela fora do ar."),
            onError: (e) => aoAvisar(mensagem(e)),
          })
          // Pôr no ar o que ainda não foi gravado publicaria a versão antiga: grava antes, e
          // se o PUT falhar não publica (o erro dele já vai para o aviso).
          if (publicar && mudou) gravarTabela(publicarAgora)
          else publicarAgora()
        }}
      />

      <Secao titulo="A tabela">
        <Linha>
          <Campo rotulo="Título">
            <input value={forma.titulo ?? ""} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, titulo: e.target.value })} />
          </Campo>
          <Escolha rotulo="Sistema" mapa={nomes.sistema} prefixo="sistema"
                   valor={forma.sistemaId} aoMudar={(v) => setForma({ ...forma, sistemaId: v })} />
        </Linha>
        <Campo rotulo="Colunas" ajuda="Quantas colunas de valor a tabela tem.">
          <input type="number" value={forma.qtdColunas || ""} style={estiloCampo}
                 onChange={(e) => setForma({ ...forma, qtdColunas: Number(e.target.value) })} />
        </Campo>
        <Campo rotulo="Texto">
          <input value={forma.texto ?? ""} style={estiloCampo}
                 onChange={(e) => setForma({ ...forma, texto: e.target.value })} />
        </Campo>

        {/* **Os titulos das colunas, para tabelas que ja existem.**
            Ate aqui esta tela sabia LER a configuracao e nao editar: uma tabela sem
            configuracao mostrava "Coluna 1", "Coluna 2" para sempre, e a unica saida era
            o sistema antigo. O PUT sempre aceitou `colunas[]` -- faltava o formulario.

            A lista e montada a partir de `qtdColunas`, e nao da configuracao existente:
            tabela com 4 colunas e 2 configuradas precisa mostrar 4 campos, senao as duas
            que faltam continuam sem nome e sem jeito de dar nome. */}
        <div style={{ marginTop: 10 }}>
          <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Títulos das colunas</span>
          {Array.from({ length: Math.max(forma.qtdColunas || 0, 1) }, (_, i) => {
            const numero = i + 1
            const atual = (forma.colunas ?? []).find((c) => c.coluna === numero)
            return (
              <Campo key={numero} rotulo={`Coluna ${numero}`}>
                <input
                  value={atual?.titulo ?? ""}
                  placeholder={`aparece como "Coluna ${numero}" na grade`}
                  style={estiloCampo}
                  onChange={(e) => {
                    const texto = e.target.value
                    const antes = forma.colunas ?? []
                    const existe = antes.some((c) => c.coluna === numero)
                    // Quem nao existia entra com os mesmos padroes do cadastro; quem existia
                    // conserva tipoCampo, tolerancia e grandeza -- sao campos que esta tela
                    // nao edita, e sobrescrever com zero apagaria dado que alguem pos la.
                    const colunas = existe
                      ? antes.map((c) => (c.coluna === numero ? { ...c, titulo: texto } : c))
                      : [...antes, { coluna: numero, titulo: texto, tipoCampo: 0, tolerancia: 0 }]
                    setForma({ ...forma, colunas: colunas.sort((a, b) => a.coluna - b.coluna) })
                  }}
                />
              </Campo>
            )
          })}
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <Botao destaque ocupado={alterar.isPending} aoClicar={() => {
            aoAvisar(null)
            gravarTabela(() => aoAvisar("Tabela gravada."))
          }}>
            {alterar.isPending ? "Gravando…" : "Gravar"}
          </Botao>
          {mudou && (
            <>
              <Botao aoClicar={() => setForma({ ...tabela })}>Descartar</Botao>
              <span style={{ fontSize: 11, color: "var(--txt3)", alignSelf: "center" }}>
                Alterações não gravadas.
              </span>
            </>
          )}
          <Botao ocupado={excluir.isPending} aoClicar={() => {
            aoAvisar(null)
            excluir.mutate(base, {
              onSuccess: () => { aoAvisar(`Tabela ${tabela.id} excluída.`); aoFechar() },
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}>
            Excluir
          </Botao>
        </div>
      </Secao>

      <Secao titulo="Linhas" contagem={`${linhas.data?.length ?? 0}`}>
        {linhas.isPending ? (
          <p style={{ fontSize: 12, color: "var(--txt2)" }}>Carregando…</p>
        ) : (
          <TabelaSimples
            itens={grade}
            vazio="Esta tabela ainda não tem linha."
            recente={recente.tem}
            selecionado={emEdicao?.id ?? null}
            aoAbrir={(g) => abrirLinha(g.linha)}
            colunas={colunasDaGrade}
          />
        )}
      </Secao>

      {emEdicao && (
        // `key`: o painel de pinouts guarda filtro próprio, e ele é de uma linha só.
        <div ref={edicao} key={emEdicao.id}>
          <Secao titulo={`Alterar linha ${emEdicao.ordem}`}>
            {/* Só as colunas configuradas viram campo. Um valor além delas (tabela que
                perdeu coluna depois de ter linha) vai no PUT como veio. */}
            <form
              onSubmit={(e) => { e.preventDefault(); gravarLinha() }}
              onKeyDown={(e) => { if (e.key === "Escape") setEmEdicao(null) }}
            >
              <CamposDaLinha
                colunas={colunas}
                valores={emEdicao.valores}
                aoMudar={(indice, texto) => setEmEdicao({
                  ...emEdicao,
                  valores: emEdicao.valores.map((v, i) => (i === indice ? texto : v)),
                })}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <Botao destaque envia ocupado={daLinha.alterar.isPending} aoClicar={() => {}}>
                  {daLinha.alterar.isPending ? "Gravando…" : "Gravar"}
                </Botao>
                <Botao aoClicar={() => setEmEdicao(null)}>Cancelar</Botao>
              </div>
            </form>

            {/* **Os pinouts são da linha, e não uma coluna dela.** Até 13/10/2026 havia
                uma coluna "Pinouts" na grade, com cabeçalho igual ao das colunas de valor,
                e parecia um campo da tabela. O vínculo existe para o site — quem abre o
                componente no diagrama interativo vê a tabela —, então fica aqui, junto da
                linha aberta. */}
            <div style={{ marginTop: 14 }}>
              <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Pinouts ligados a esta linha</span>
              <PinoutsDaLinha tabelaId={tabela.id} linhaId={emEdicao.id} aoAvisar={aoAvisar} />
            </div>
          </Secao>
        </div>
      )}

      <Secao titulo="Incluir linha">
        <form onSubmit={(e) => { e.preventDefault(); incluirLinha() }}>
          <CamposDaLinha
            colunas={colunas}
            valores={valoresDaNova}
            primeiro={primeiroDaNova}
            aoMudar={(indice, texto) => setNova(valoresDaNova.map((v, i) => (i === indice ? texto : v)))}
          />
          <Botao destaque envia desligado={novaVazia} ocupado={daLinha.criar.isPending} aoClicar={() => {}}>
            Incluir
          </Botao>
        </form>
      </Secao>

      <Secao titulo="Veículos em que vale" contagem={`${veiculos.data?.length ?? 0}`}>
        <TabelaSimples
          itens={veiculos.data ?? []}
          vazio="Nenhum veículo vinculado — a tabela não aparece para ninguém assim."
          colunas={[
            { chave: "veiculoModelo", rotulo: "Veículo" },
            { chave: "motorNome", rotulo: "Motor" },
            { chave: "anoInicial", rotulo: "De", numero: true, largura: 70, render: (v) => anoDe(v.anoInicial) },
            { chave: "anoFinal", rotulo: "Até", numero: true, largura: 70, render: (v) => anoDe(v.anoFinal) },
            {
              chave: "id", rotulo: "", largura: 50,
              render: (v) => (
                <button
                  onClick={() => {
                    aoAvisar(null)
                    excluir.mutate(`${base}/veiculos/${v.id}`, {
                      onSuccess: () => aoAvisar("Vínculo removido."),
                      onError: (x) => aoAvisar(mensagem(x)),
                    })
                  }}
                  style={{
                    border: 0, background: "transparent", cursor: "pointer",
                    color: "var(--txt3)", fontSize: 13, lineHeight: 1,
                  }}
                >
                  ×
                </button>
              ),
            },
          ]}
        />

        <Linha>
          <Escolha rotulo="Veículo" mapa={nomes.veiculo} prefixo="veículo"
                   valor={novoVeiculo.veiculoId}
                   aoMudar={(v) => setNovoVeiculo({ ...novoVeiculo, veiculoId: v })} />
          <Escolha rotulo="Motor" mapa={nomes.motor} prefixo="motor"
                   valor={novoVeiculo.motorId}
                   aoMudar={(v) => setNovoVeiculo({ ...novoVeiculo, motorId: v })} />
          <Campo rotulo="Ano inicial">
            <input type="number" value={novoVeiculo.anoInicial || ""} style={estiloCampo}
                   onChange={(e) => setNovoVeiculo({ ...novoVeiculo, anoInicial: Number(e.target.value) })} />
          </Campo>
          <Campo rotulo="Ano final">
            <input type="number" value={novoVeiculo.anoFinal || ""} style={estiloCampo}
                   onChange={(e) => setNovoVeiculo({ ...novoVeiculo, anoFinal: Number(e.target.value) })} />
          </Campo>
        </Linha>
        {anosInvertidos(novoVeiculo) && (
          <p style={{ margin: "0 0 7px", fontSize: 11, color: "var(--erro)" }}>
            O ano inicial é depois do ano final.
          </p>
        )}
        {/* O motor também é obrigatório: sem ele a API devolve "O motor é obrigatório". */}
        <Botao
          desligado={novoVeiculo.veiculoId <= 0 || novoVeiculo.motorId <= 0 || anosInvertidos(novoVeiculo)}
          ocupado={criar.isPending}
          aoClicar={() => {
            aoAvisar(null)
            // Aqui o ano é `date` no banco. O acervo grava o início em 1º de janeiro e o
            // fim em 31 de dezembro (2021-01-01 a 2021-12-31), e é o que vai.
            criar.mutate({
              rota: `${base}/veiculos`,
              dados: {
                veiculoId: novoVeiculo.veiculoId,
                motorId: novoVeiculo.motorId,
                anoInicial: novoVeiculo.anoInicial > 0 ? `${novoVeiculo.anoInicial}-01-01` : null,
                anoFinal: novoVeiculo.anoFinal > 0 ? `${novoVeiculo.anoFinal}-12-31` : null,
              },
            }, {
              onSuccess: () => { setNovoVeiculo(VEICULO_VAZIO); aoAvisar("Veículo vinculado.") },
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}
        >
          Vincular veículo
        </Botao>
      </Secao>
    </Painel>
  )
}

/** Um campo por coluna da tabela — os mesmos para incluir e para alterar a linha. */
function CamposDaLinha({ colunas, valores, aoMudar, primeiro }: {
  colunas: ColunaDeValor[]
  valores: (string | null)[]
  aoMudar: (indice: number, texto: string) => void
  primeiro?: React.Ref<HTMLInputElement>
}) {
  return (
    <Linha>
      {colunas.map((c) => (
        <Campo key={c.indice} rotulo={c.rotulo}>
          <input
            ref={c.indice === 0 ? primeiro : undefined}
            value={valores[c.indice] ?? ""}
            style={estiloCampo}
            onChange={(e) => aoMudar(c.indice, e.target.value)}
          />
        </Campo>
      ))}
    </Linha>
  )
}
